'use client';

import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { ClickAwayListener } from '@packages/shared/src/ui/ClickAwayListener/ClickAwayListener';
import { cn } from '@packages/shared/src/utils/cn/cn';
import DOMPurify from 'isomorphic-dompurify';
import { useId, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { toolTipStyles } from './Tooltip.styles';
import { calculateNewPositioning } from './calculateNewPositioning';
import type { TooltipProps } from './TooltipProps';

const messageMoreInformation = defineMessage({
  id: 'navigation.tooltip.cms.back.aria-label',
  defaultMessage: 'Weitere Informationen anzeigen',
});

const Tooltip = ({
  children,
  content: tooltipContent,
  variant,
  positioning = 'top-start',
}: TooltipProps) => {
  const positioningInfoIcon = variant === 'inline' ? null : positioning;
  const tooltipId = useId();
  const { formatMessage } = useIntl();
  const tooltipContentRef = useRef<HTMLSpanElement>(null);
  /* this state is used to store the current positioning of the tooltip (modified by the intersection observer and the initial click to open).
    It is initialized with undefined to indicate the tooltip should not be visible */
  const [positioningTooltip, setPositioningTooltip] = useState<TooltipProps['positioning']>();

  // on vertical scroll toggle the positioning of the tooltip if it is intersecting
  useIntersectionObserver(tooltipContentRef, {
    intersectOnce: false,
    threshold: 1.0,
    callback: (isIntersecting, scrolledTop) => {
      if (!isIntersecting && !tooltipContentRef.current?.hidden) {
        setPositioningTooltip((prev) => {
          if (prev?.includes('top') && scrolledTop) {
            return prev.replace('top', 'bottom') as typeof prev;
          }
          if (prev?.includes('bottom') && !scrolledTop) {
            return prev.replace('bottom', 'top') as typeof prev;
          }
          return prev;
        });
      }
    },
  });

  if (!tooltipContent) {
    return children;
  }

  return (
    <div
      className={cn(
        variant === 'inline' ? 'inline-flex' : 'flex',
        positioning &&
          ['top-start', 'bottom-start', 'left'].includes(positioning) &&
          'flex-row-reverse',
      )}
    >
      {children /* the container the tooltip belongs to */}
      <ClickAwayListener
        onClickAway={() => {
          setPositioningTooltip(undefined);
        }}
        strategy="click"
        asChild
      >
        <div
          className={cn(
            'text-size-h4 relative flex flex-col p-0.5',
            variant === 'inline' ? 'relative' : 'absolute',
            positioningInfoIcon === 'left' && 'top-1/2 left-1',
            positioningInfoIcon === 'right' && 'top-1/2 right-1',
            positioningInfoIcon === 'top-start' && 'top-1 left-1',
            positioningInfoIcon === 'top-end' && 'top-1 right-1',
            positioningInfoIcon === 'bottom-start' && 'bottom-1 left-1',
            positioningInfoIcon === 'bottom-end' && 'right-1 bottom-1',
            positioningTooltip &&
              ['right', 'top-end', 'bottom-end'].includes(positioningTooltip) &&
              'items-end',
          )}
        >
          <Button
            aria-label={formatMessage(messageMoreInformation)}
            aria-controls={tooltipId}
            aria-expanded={!!positioningTooltip}
            className={cn(
              positioningInfoIcon?.includes('start') && 'ml-1',
              positioningInfoIcon?.includes('end') && 'mr-1',
              'bg-color-info-light border-color-info-dark hover:bg-color-info! border-1 border-solid',
              positioningTooltip && 'shadow-lg',
            )}
            variant="icon-flat"
            size="base"
            onClick={() => {
              // toggle tooltip visibility before setting the new positioning -> the calculation needs a visible tooltip
              if (tooltipContentRef.current) {
                tooltipContentRef.current.hidden = !tooltipContentRef.current.hidden;
              }
              setPositioningTooltip((prevPositioning) => {
                // just reset the positioning to undefined if the tooltip has a position currently assigned
                if (prevPositioning) {
                  return undefined;
                }
                return calculateNewPositioning(
                  tooltipContentRef,
                  prevPositioning ||
                    // in inline variant the tooltip should always be benath the text
                    (variant === 'inline' ? 'top-start' : positioning) ||
                    'top-start',
                );
              });
            }}
          >
            <span className="icon-emp-info bg-color-info-dark hover:bg-color-info-light" />
          </Button>
          <span
            hidden={!positioningTooltip || undefined}
            ref={tooltipContentRef}
            id={tooltipId}
            className={cn(
              'z-tooltip text-size-body3 bg-color-text-light text-color-text-dark absolute m-0.5 w-[18.75rem] max-w-[70vw] rounded p-1 font-normal shadow-lg before:absolute',
              positioningTooltip === 'top-start' && toolTipStyles.toolTipBottomRight,
              positioningTooltip === 'top-end' && toolTipStyles.toolTipBottomLeft,
              positioningTooltip === 'left' && toolTipStyles.toolTipRight,
              positioningTooltip === 'right' && toolTipStyles.toolTipLeft,
              positioningTooltip === 'bottom-start' && toolTipStyles.toolTipTopRight,
              positioningTooltip === 'bottom-end' && toolTipStyles.toolTipTopLeft,
            )}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tooltipContent) }}
          />
        </div>
      </ClickAwayListener>
    </div>
  );
};

Tooltip.displayName = 'Tooltip';

export { Tooltip };
